import React, { HTMLAttributes } from 'react'
import styled, { css, DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import { above } from '@styles/mixins/breakpoints'

export interface IButton extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  icon?: React.ReactNode
  link?: boolean
  href?: string
  text?: string
  type?: 'button' | 'reset' | 'submit' | undefined
  target?: '_self' | '_blank' | '_parent' | '_top'
  disabled?: boolean
}

const SIcon = styled.span`
  display: flex;
  margin-right: 6px;
  align-items: center;
`

export interface IButtonProps extends SpaceProps {
  variant?: 'primary' | 'secondary'
}

const Button: React.FC<IButton> = ({ children, text, icon, link = false, disabled, ...tagProps }) => {
  const child = (
    <>
      {icon && <SIcon>{icon}</SIcon>}
      {children}
    </>
  )
  return link ? (
    text ? (
      <a {...tagProps} dangerouslySetInnerHTML={{ __html: text }}></a>
    ) : (
      <a {...tagProps}>{child}</a>
    )
  ) : (
    <button {...tagProps} disabled={disabled}>
      {child}
    </button>
  )
}

const SButtonBase = styled(Button)<SpaceProps>`
  border-width: 1px;
  border-style: solid;
  border-radius: ${({ theme }) => theme.general.borderRadius};
  display: flex;
  align-items: center;
  cursor: pointer;
  outline: none;
  transition: 0.2s;
  user-select: none;

  ${SIcon} {
    flex-shrink: 0;
    flex-grow: 0;
  }
  ${space}
`

export const SButton = styled(SButtonBase)<IButtonProps>`
  border-color: black;
  background-color: black;
  color: white;
  padding: 14px 23px 12px;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  svg path {
    fill: white;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.green};
    background-color: ${({ theme }) => theme.colors.green};
  }

  // Theme
  ${(props) => {
    switch (props.variant) {
      case 'primary':
        return css`
          background-color: ${props.theme.colors.black};
          color: ${props.theme.colors.white};
          border: 1px solid ${props.theme.colors.black};

          :hover,
          :focus {
            background-color: ${props.theme.colors.grass};
            border-color: ${props.theme.colors.grass};
            color: ${props.theme.colors.white};
          }

          :active {
            background-color: ${props.theme.colors.green};
            border-color: ${props.theme.colors.green};
          }

          :disabled {
            border-color: ${props.theme.colors.middleGray};
            background-color: ${props.theme.colors.middleGray};

            :hover,
            :focus,
            :active {
              border-color: ${props.theme.colors.middleGray};
              background-color: ${props.theme.colors.middleGray};
            }
          }
        `
      case 'secondary':
        return css`
          background-color: transparent;
          border: 1px solid ${props.theme.colors.black};
          color: ${props.theme.colors.black};

          svg path {
            fill: black;
          }

          :hover,
          :focus {
            border-color: ${props.theme.colors.grass};
            background-color: transparent;
            color: ${props.theme.colors.black};
          }

          :active {
            border-color: ${props.theme.colors.green};
          }

          :disabled {
            color: ${props.theme.colors.gray};
            border-color: ${props.theme.colors.gray};

            :hover,
            :focus,
            :active {
              border-color: ${props.theme.colors.gray};
            }
          }
        `
      default:
        return css`
          background-color: ${props.theme.colors.black};
          color: ${props.theme.colors.white};
          border: 1px solid ${props.theme.colors.black};

          :hover,
          :focus {
            background-color: ${props.theme.colors.grass};
            border-color: ${props.theme.colors.grass};
            color: ${props.theme.colors.white};
          }

          :active {
            background-color: ${props.theme.colors.green};
            border-color: ${props.theme.colors.green};
          }

          :disabled {
            border-color: ${props.theme.colors.middleGray};
            background-color: ${props.theme.colors.middleGray};

            :hover,
            :focus,
            :active {
              border-color: ${props.theme.colors.middleGray};
              background-color: ${props.theme.colors.middleGray};
            }
          }
        `
    }
  }}
`

type THeaderButton = 'primary' | 'secondary' | 'black' | 'primaryGreen'
type THeaderButtonPresets = {
  [key in THeaderButton]: FlattenInterpolation<ThemeProps<DefaultTheme>>
}

const headerButtonPresets: THeaderButtonPresets = {
  primary: css`
    border-color: ${({ theme }) => theme.colors.green};
    background-color: ${({ theme }) => theme.colors.green};
    color: white;

    &:hover {
      border-color: black;
      color: white;
      background-color: black;
    }
  `,
  secondary: css`
    border-color: transparent;
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.green};

    svg path {
      fill: ${({ theme }) => theme.colors.green};
      transition: 0.2s;
    }

    &:hover {
      border-color: ${({ theme }) => theme.colors.green};
      background-color: ${({ theme }) => theme.colors.green};
      color: white;

      svg path {
        fill: white;
      }
    }
  `,
  primaryGreen: css`
    border-color: ${({ theme }) => theme.colors.green};
    background-color: ${({ theme }) => theme.colors.green};
    color: white;

    &:hover {
      border-color: ${({ theme }) => theme.colors.darkGreen};
      background-color: ${({ theme }) => theme.colors.darkGreen};
      color: white;
    }
  `,
  black: css`
    border-color: black;
    background: white;
    color: black;

    &:hover {
      background-color: black;
      color: white;
    }
  `,
}

export const SButtonHeader = styled(SButtonBase)<{ type: THeaderButton }>`
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  padding: ${({ icon }) => (icon ? '6px 12px 6px 8px' : '6px 12px')};

  ${({ type }) => headerButtonPresets[type]}

  ${SIcon} {
    margin-right: 5px;
    height: 16px;
  }
`

interface IButtonTabProps {
  active?: boolean | number
}

export const SButtonTab = styled(SButtonBase)<IButtonTabProps>`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  padding: 7px 16px;
  background: ${({ theme }) => theme.colors.lightGray};
  border: none;

  ${({ active }) =>
    active
      ? css`
          background: ${({ theme }) => theme.colors.green};
          color: white;
        `
      : ''}

  &:hover {
    background: ${({ theme }) => theme.colors.green};
    color: white;
  }
`
